import { RectangleView } from '@st/document-annotations'
import { HTMLSurface, Viewport } from '@st/document-rendering'
import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useMove,
  useUpdateBounds,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { move, Offset, Point, rectFromPoints } from '@st/geom'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { Rect, RectangleAnnotation } from '@st/sdk'
import { THEME_COLORS } from '@st/theme/colors'
import { uuidv7 } from '@st/util/uuidv7'
import { useState } from 'react'
import { DeleteIcon } from '~/components/icons'
import { RectToolState } from '../../annotations'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { InlineColorPicker } from '../../inline-color-picker'

export const RECT_COLOR_OPTIONS = [
  THEME_COLORS['red'][600],
  THEME_COLORS['blue'][500],
  THEME_COLORS['orange'][500],
  THEME_COLORS['green'][500],
  THEME_COLORS['gray'][900]
]

export function RectangleToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: RectToolState
  activeAnnotation: RectangleAnnotation | undefined
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  return (
    <InlineColorPicker
      options={RECT_COLOR_OPTIONS}
      value={toolState.settings.border.color}
      onChange={(color) => {
        if (activeAnnotation) {
          const newAnnotation: RectangleAnnotation = {
            ...activeAnnotation,
            border: { ...activeAnnotation.border, color: color }
          }
          send({
            type: 'updateAnnotationDraft',
            annotationDraft: newAnnotation
          })
          send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
        } else {
          send({
            type: 'updateTool',
            tool: {
              ...toolState,
              settings: {
                ...toolState.settings,
                border: { ...toolState.settings.border, color: color }
              }
            }
          })
        }
      }}
    />
  )
}

export function copyToolState(annotation: RectangleAnnotation): RectToolState {
  return {
    name: 'rectangle',
    settings: { border: annotation.border }
  }
}

type CreateRectState =
  | { status: 'idle' }
  | {
      status: 'drawing'
      initialPoint: Point
      draggingPoint: Point
      annotation: RectangleAnnotation
    }

export function RectangleToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: RectToolState
  activeAnnotation: RectangleAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  const [state, setState] = useState<CreateRectState>({ status: 'idle' })

  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove: (e) => {
      switch (e.type) {
        case 'start':
          setState({
            status: 'drawing',
            initialPoint: e.start.point,
            draggingPoint: e.start.point,
            annotation: {
              id: uuidv7(),
              time: new Date().toISOString(),
              type: 'rectangle',
              pageNumber: page.pageNumber,
              bounds: {
                x: e.start.point.x,
                y: e.start.point.y,
                width: 0,
                height: 0
              },
              border: toolState.settings.border
            }
          })
          break
        case 'move':
        case 'end':
          if (state.status !== 'drawing') return state

          const updatedAnnotation: RectangleAnnotation = {
            ...state.annotation,
            bounds: constrainRectResize(
              rectFromPoints(state.initialPoint, e.current.point),
              page.bounds
            )
          }

          setState({
            status: 'drawing',
            initialPoint: state.initialPoint,
            draggingPoint: e.current.point,
            annotation: updatedAnnotation
          })

          if (e.type === 'end') {
            if (updatedAnnotation.bounds.width > 10 || updatedAnnotation.bounds.height > 10) {
              send({
                type: 'createAnnotationDraft',
                annotationDraft: updatedAnnotation,
                elementStatus: 'none'
              })
              send({ type: 'createAnnotation', annotationDraft: updatedAnnotation })
            }
            setState({ status: 'idle' })
          }
          break
      }
    }
  })

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        cursor: 'crosshair',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
        } else {
          onPointerDown(e)
        }
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {state.status === 'drawing' && (
        <ViewElementContainer key={state.annotation.id} bounds={state.annotation.bounds}>
          <RectangleAnnotationDisplay annotation={state.annotation} />
        </ViewElementContainer>
      )}
    </div>
  )
}

export function RectangleAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: RectangleAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  if (status == 'editing' || status == 'selected') {
    return <RectangleAnnotationEdit annotation={annotation} page={page} send={send} />
  }
  return <RectangleAnnotationView annotation={annotation} send={send} />
}

function RectangleAnnotationView({
  annotation,
  send
}: {
  annotation: RectangleAnnotation
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  return (
    <ViewElementContainer
      key={annotation.id}
      bounds={annotation.bounds}
      onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
    >
      <RectangleAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function RectangleAnnotationEdit({
  annotation,
  page,
  send
}: {
  annotation: RectangleAnnotation
  page: PageStructure
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  function handleNudge(offset: Partial<Offset>) {
    send({
      type: 'updateAnnotationDraft',
      annotationDraft: {
        ...annotation,
        bounds: constrainRectMove(move(annotation.bounds, offset), page.bounds)
      }
    })
  }

  useKeyboardShortcuts({
    Backspace: () => {
      send({ type: 'deleteAnnotation', annotationId: annotation.id })
    },
    ArrowUp: () => handleNudge({ dy: -10 }),
    ArrowDown: () => handleNudge({ dy: 10 }),
    ArrowLeft: () => handleNudge({ dx: -10 }),
    ArrowRight: () => handleNudge({ dx: 10 })
  })

  const { onMove, onResize } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      let constrainedBounds =
        event.type == 'move'
          ? constrainRectMove(event.newBounds, page.bounds)
          : constrainRectResize(event.newBounds, page.bounds)

      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: constrainedBounds }
      })
    },
    onCommit: (event) => {
      let constrainedBounds =
        event.type == 'move'
          ? constrainRectMove(event.newBounds, page.bounds)
          : constrainRectResize(event.newBounds, page.bounds)

      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: constrainedBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      key={annotation.id}
      bounds={annotation.bounds}
      onMove={onMove}
      onResize={onResize}
      resizable={true}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => send({ type: 'deleteAnnotation', annotationId: annotation.id })}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <RectangleAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function RectangleAnnotationDisplay({ annotation }: { annotation: RectangleAnnotation }) {
  return (
    <div
      style={{
        position: 'relative',
        width: annotation.bounds.width,
        height: annotation.bounds.height
      }}
    >
      <Viewport
        Surface={HTMLSurface}
        width={annotation.bounds.width}
        height={annotation.bounds.height}
      >
        <RectangleView annotation={annotation} />
      </Viewport>
    </div>
  )
}

function constrainRectResize(rect: Rect, pageBounds: Rect): Rect {
  const topLeft: Point = {
    x: Math.max(rect.x, pageBounds.x),
    y: Math.max(rect.y, pageBounds.y)
  }

  const bottomRight: Point = {
    x: Math.min(rect.x + rect.width, pageBounds.x + pageBounds.width),
    y: Math.min(rect.y + rect.height, pageBounds.y + pageBounds.height)
  }

  return rectFromPoints(topLeft, bottomRight)
}

// Constrains a rectangle position during move operations to stay within page bounds
// This maintains the original size and only adjusts position
function constrainRectMove(rect: Rect, pageBounds: Rect): Rect {
  const result = { ...rect }

  // Calculate the maximum allowed x and y positions
  const maxX = pageBounds.x + pageBounds.width - rect.width
  const maxY = pageBounds.y + pageBounds.height - rect.height

  // Clamp the position to keep the rectangle fully within bounds
  result.x = Math.max(pageBounds.x, Math.min(rect.x, maxX))
  result.y = Math.max(pageBounds.y, Math.min(rect.y, maxY))

  return result
}
